'use client'

import { useEffect, useState } from 'react'
import { Divider, Spacer, Text } from '@vinted/web-ui'

import {
  FormName,
  GoogleTagManagerEvent,
  PageType,
  useGoogleTagManagerTrack,
} from '@marketplace-web/google-track/google-track-feature'
import { useBrowserNavigation } from '@marketplace-web/browser/browser-navigation-util'
import { useFeatureSwitch } from '@marketplace-web/feature-flags/feature-switches-data'
import { useUserAgent } from '@marketplace-web/environment/request-context-data'
import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'
import { ContentLoader } from '@marketplace-web/common-components/content-loader-ui'

import {
  getReferrer,
  ReferrerModel,
  transformReferrerDto,
} from '@marketplace-web/referrals/referrals-data'
import { useAbTest, useTrackAbTestCallback } from '@marketplace-web/feature-flags/ab-tests-data'
import { useSession } from '@marketplace-web/shared/session-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'

import Facebook from '../Facebook'
import Google from '../Google'
import Apple from '../Apple'
import BusinessRegistrationLink from './BusinessRegistrationsLink'
import SelectTypeFooter from './SelectTypeFooter'
import SelectTypeHeader from './SelectTypeHeader'
import useAuthenticationContext from '../../hooks/useAuthenticationContext'
import { AuthView } from '../../constants'
import RandomUserSignup from '../RandomUserSignup'
import useAuthModal from '../../hooks/useAuthModal/useAuthModal'
import { isIOS, parseOS } from '../../utils/device'

const SelectType = () => {
  const isRandomUserSignupEnabled = useFeatureSwitch('next_random_user_signup')
  const { authView, isAuthPage } = useAuthenticationContext()
  const { isBusinessAuth } = useAuthModal()
  const isRegisterView = authView === AuthView.SelectTypeRegister
  const { googleAnalyticsTrack } = useGoogleTagManagerTrack()
  const { isWebview } = useSession()
  const translate = useTranslate('auth.select_type')

  const { s: registrationType } = useBrowserNavigation().searchParams
  const userAgent = useUserAgent()
  const isAppleDevice = isIOS(userAgent) || parseOS(userAgent) === 'MacOS'

  const [error, setError] = useState<string>()
  const [hasReferralError, setHasReferralError] = useState(false)
  const [referrer, setReferrer] = useState<ReferrerModel>()

  const trackAbTest = useTrackAbTestCallback()
  const nonNativeFlowAbTest = useAbTest('non_native_flow')

  useEffect(() => {
    if (isWebview || isBusinessAuth) return

    trackAbTest(nonNativeFlowAbTest)
  }, [isBusinessAuth, isWebview, nonNativeFlowAbTest, trackAbTest])

  useEffect(() => {
    function pushGoogleAnalyticsEvent() {
      if (isRegisterView) {
        googleAnalyticsTrack({
          event: GoogleTagManagerEvent.modalView,
          customTagObject: { formName: FormName.Register, pageType: PageType.Registration },
        })
      } else {
        googleAnalyticsTrack({
          event: GoogleTagManagerEvent.modalView,
          customTagObject: { formName: FormName.Login, pageType: PageType.Login },
        })
      }
    }

    pushGoogleAnalyticsEvent()
  }, [isRegisterView, googleAnalyticsTrack])

  const shouldFetchReferrer = isRegisterView && !referrer && registrationType === 'referral'

  useEffect(() => {
    if (!shouldFetchReferrer) return

    const fetchReferrer = async () => {
      const response = await getReferrer()

      if ('errors' in response || !response.referrer_id) {
        setHasReferralError(true)

        return
      }

      setReferrer(transformReferrerDto(response))
    }

    fetchReferrer()
  }, [shouldFetchReferrer])

  if (!hasReferralError && shouldFetchReferrer) {
    return (
      <ContentLoader
        styling={ContentLoader.Styling.Wide}
        size={ContentLoader.Size.Large}
        testId="select-type-loader"
      />
    )
  }

  const renderDivider = () => {
    if (!isWebview) return null

    return (
      <>
        <Spacer />
        <div className="u-flexbox u-flex-direction-row u-align-items-center u-gap-large">
          <Divider />
          <Text
            as="span"
            text={translate('divider.or')}
            type={Text.Type.Subtitle}
            alignment={Text.Alignment.Center}
            theme="muted"
          />
          <Divider />
        </div>
        <Spacer />
      </>
    )
  }

  const renderSocialRegisterSubheadline = () => {
    if (!isWebview || !isRegisterView) return null

    return (
      <>
        <Spacer size={Spacer.Size.Small} />
        <Text
          as="h2"
          text={translate(
            isAppleDevice ? 'register.subheadline_apple' : 'register.subheadline_google',
          )}
          type={Text.Type.Subtitle}
          alignment={Text.Alignment.Center}
          width={Text.Width.Parent}
        />
        <Spacer />
      </>
    )
  }

  const renderSocialLogins = () => {
    if (isAppleDevice) {
      return (
        <SeparatedList
          separator={<Spacer size={isAuthPage ? Spacer.Size.Large : Spacer.Size.Regular} />}
        >
          <Apple setError={setError} />
          {renderDivider()}
          <Google setError={setError} />
          <Facebook setError={setError} />
          {isRandomUserSignupEnabled && isRegisterView && <RandomUserSignup />}
        </SeparatedList>
      )
    }

    return (
      <SeparatedList
        separator={<Spacer size={isAuthPage ? Spacer.Size.Large : Spacer.Size.Regular} />}
      >
        <Google setError={setError} />
        {renderDivider()}
        {!isWebview && <Apple setError={setError} />}
        <Facebook setError={setError} />
        {isRandomUserSignupEnabled && isRegisterView && <RandomUserSignup />}
      </SeparatedList>
    )
  }

  return (
    <>
      <div className="u-ui-padding-horizontal-large u-ui-padding-bottom-x-large">
        <SelectTypeHeader referrer={referrer} />
        <Spacer />
        {error && (
          <Text
            as="span"
            text={error}
            theme="warning"
            width={Text.Width.Parent}
            alignment={Text.Alignment.Center}
            html
          />
        )}
        {renderSocialRegisterSubheadline()}
        <Spacer size={Spacer.Size.Large} />
        {renderSocialLogins()}
        <Spacer size={isAuthPage ? Spacer.Size.Large : Spacer.Size.XLarge} />
        <SelectTypeFooter />
      </div>
      {isRegisterView && <BusinessRegistrationLink />}
    </>
  )
}

export default SelectType
